import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../config/axios';

function EditProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const newPreviews = useMemo(() => newImages.map((image) => URL.createObjectURL(image)), [newImages]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/products/${id}`);
        setProduct(data);
        setName(data.name);
        setDescription(data.description);
        setPrice(data.price);
        setQuantity(data.quantity);
        setExistingImages(data.images || []);
      } catch (err) {
        setError('Could not load this listing.');
      }
    };

    fetchProduct();
  }, [id]);

  const handleImageChange = (e) => {
    const selectedFiles = [...e.target.files];
    if (selectedFiles.length + existingImages.length > 5) {
      setError('A listing can have up to 5 images.');
      return;
    }
    setError('');
    setNewImages(selectedFiles);
  };

  const handleImageRemove = (index) => {
    setExistingImages((images) => images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (existingImages.length + newImages.length < 1) {
      setError('Keep at least one photo on the listing.');
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description);
    formData.append('price', price);
    formData.append('quantity', quantity);
    formData.append('existingImages', JSON.stringify(existingImages));
    newImages.forEach((image) => formData.append('images', image));

    try {
      setSubmitting(true);
      await axiosInstance.put(`/api/products/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/my-products');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update this listing.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!product && !error) {
    return <div className="page-shell"><div className="state-card">Loading listing editor...</div></div>;
  }

  return (
    <div className="page-shell">
      <section className="form-layout">
        <div className="form-intro">
          <span className="eyebrow">Manage listing</span>
          <h1>Update product details and stock.</h1>
          <p>Keep the listing accurate so buyers know exactly what is available.</p>
        </div>

        <form onSubmit={handleSubmit} className="panel-form">
          {error && <div className="notice error">{error}</div>}
          <label>
            Product Name
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label>
            Description
            <textarea rows="5" value={description} onChange={(e) => setDescription(e.target.value)} required />
          </label>
          <div className="form-grid-two">
            <label>
              Price
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </label>
            <label>
              Quantity
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(Math.min(200, Math.max(0, Number(e.target.value) || 0)))}
                min="0"
                max="200"
                required
              />
            </label>
          </div>

          {existingImages.length > 0 && (
            <div>
              <span className="field-label">Current Photos</span>
              <div className="preview-grid editable">
                {existingImages.map((image, index) => (
                  <div key={image} className="preview-tile">
                    <img src={image} alt={`Product ${index + 1}`} />
                    <button type="button" onClick={() => handleImageRemove(index)}>Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <label>
            Add Photos
            <input type="file" accept="image/*" multiple onChange={handleImageChange} />
            <span className="field-note">Up to 5 total images.</span>
          </label>

          {newPreviews.length > 0 && (
            <div className="preview-grid">
              {newPreviews.map((preview, index) => (
                <img key={preview} src={preview} alt={`New preview ${index + 1}`} />
              ))}
            </div>
          )}

          <button type="submit" className="button button-primary" disabled={submitting}>
            {submitting ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </section>
    </div>
  );
}

export default EditProductPage;
